import React, { useRef, useState } from 'react';
import { Flex } from 'antd';
import { faArrowRight, faLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useParams } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { StudyClient } from 'jf/api';
import { DevExThemeProvider, useDevExTheme } from 'jf/common/themes/DevExTheme';
import { CopyLinkPopover } from 'jf/components/CopyLinkPopover';
import { useClientMutation } from 'jf/utils/useClientQuery';

import { makeStudyAnalyzerPath, SHARE_LINK_EXPIRY_DAYS } from '../StudyAnalyzerPage';

import { usePageIndexTracker } from './studyReveal';

import JFLogo from 'jf/images/jf-logo.svg';

export const RevealEnd: React.FC = () => {
  const theme = useDevExTheme();
  const navigate = useNavigate();
  const { studyRef, teamRef } = useParams<{ studyRef: string; teamRef: string }>();
  const targetRef = useRef(null);

  const [shareLink, setShareLink] = useState('');
  const { mutateAsync: shareStudy } = useClientMutation(StudyClient.shareStudy);

  const scopeToken = new URLSearchParams(window.location.search).get('scope_token');
  const isDemoView = scopeToken === 'DEMO';

  usePageIndexTracker(targetRef, 'reveal-end');

  return (
    <Flex
      style={{
        backgroundColor: theme.color.reveal.dark.background,
        width: '100vw',
        height: '101vh',
        fontSize: '64px',
        fontFamily: theme.variable.fontFamily.secondary,
        color: theme.color.reveal.accent,
        alignItems: 'center',
        justifyContent: 'center',
      }}
      ref={targetRef}
      gap={theme.variable.spacing.xxl}
    >
      {window.dx.user?.isCustomerAdmin ? (
        <>
          <DevExThemeProvider themeKey="dark">
            <CopyLinkPopover
              title="Share Reveal"
              description={`The link above will grant public access to this reveal to whomever you share it with. It will expire in ${SHARE_LINK_EXPIRY_DAYS} days.`}
              link={shareLink}
              placement="bottomRight"
              onCopy={() => trackEvent('survey-reveal:copy-link:click')}
            >
              <Flex
                style={{
                  cursor: 'pointer',
                }}
                gap={theme.variable.spacing.lg}
                onClick={() => {
                  trackEvent('survey-reveal:share:click');
                  if (isDemoView) {
                    // Don't request a new sharing token when viewing the demo version
                    // of the Study Reveal view.
                    const queryParams = new URLSearchParams(window.location.search);
                    setShareLink(
                      `${window.location.origin}${window.location.pathname}?${queryParams}`
                    );
                  } else if (studyRef) {
                    shareStudy({
                      studyRef,
                      requestBody: {
                        teamRef,
                        expiryDays: SHARE_LINK_EXPIRY_DAYS,
                      },
                    }).then((response) => {
                      const queryParams = new URLSearchParams(window.location.search);
                      queryParams.append('scope_token', response.scopeToken);
                      setShareLink(
                        `${window.location.origin}${window.location.pathname}?${queryParams}`
                      );
                    });
                  }
                }}
              >
                Share
                <FontAwesomeIcon icon={faLink} />
              </Flex>
            </CopyLinkPopover>
          </DevExThemeProvider>
          <Flex
            style={{
              cursor: 'pointer',
            }}
            gap={theme.variable.spacing.lg}
            onClick={() => {
              trackEvent('survey-reveal:explore:click');
              navigate(makeStudyAnalyzerPath(studyRef, undefined, 'insights'));
            }}
          >
            Explore
            <FontAwesomeIcon icon={faArrowRight} />
          </Flex>
        </>
      ) : (
        <Flex align="center" justify="center">
          {/* jf logo */}
          <JFLogo
            height="64px"
            color={theme.color.reveal.accent}
            style={{ paddingInlineEnd: theme.variable.spacing.sm }}
          />
          <div
            style={{
              fontSize: '64px',
              fontFamily: theme.variable.fontFamily.secondary,
              gap: theme.variable.spacing.lg,
            }}
          >
            <span style={{ fontWeight: 'bold', color: theme.color.reveal.dark.text }}>DevEx</span>
          </div>
        </Flex>
      )}
    </Flex>
  );
};
